import { useState, useEffect, useRef, useCallback } from "react";
import { useWebSocket } from "./useWebSocket.js";
import { api } from "../api/client.js";

const MAX_IN_MEMORY = 200;

/**
 * Loads alert history from the API and receives new alerts via WebSocket.
 *
 * Returns:
 *   alerts      — array of alert objects (newest first)
 *   loading     — initial fetch in progress
 *   unreadCount — alerts received since clearUnread() was last called
 *   clearUnread — resets unreadCount to 0
 */
export function useAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Track whether initial load is done to avoid counting history as unread
  const initialLoadDoneRef = useRef(false);

  // Fetch initial history
  useEffect(() => {
    api
      .getAlerts({ limit: 100 })
      .then((data) => {
        setAlerts(Array.isArray(data) ? data : []);
        initialLoadDoneRef.current = true;
      })
      .catch(() => {
        initialLoadDoneRef.current = true;
      })
      .finally(() => setLoading(false));
  }, []);

  const handleMessage = useCallback((msg) => {
    if (msg.type !== "alert") return;

    const alert = {
      id: msg.id,
      imei: msg.imei,
      plate: msg.plate ?? null,
      type: msg.alert_type,
      payload: msg.payload ?? {},
      occurred_at: msg.occurred_at,
    };

    setAlerts((prev) => [alert, ...prev].slice(0, MAX_IN_MEMORY));

    if (initialLoadDoneRef.current) {
      setUnreadCount((n) => n + 1);
    }
  }, []);

  useWebSocket(handleMessage);

  const clearUnread = useCallback(() => setUnreadCount(0), []);

  return { alerts, loading, unreadCount, clearUnread };
}
